import { getSessionToken, getWebSdk } from "@/lib/pw-auth";
import {
  PI_EXPOSURE_SOURCE,
  PI_LANDING_PAGE_NAME,
  PI_LANDING_VERSION,
  PiLandingEventType,
  type PiCloseMethod,
  type PiCtaLocation,
  type PiCtaName,
  type PiDestination,
  type PiExposureSource,
  type PiLoginAction,
  type PiLoginEntryPoint,
} from "@/lib/analytics/schema";

/** Mirrors `EVENT_PLATFORMS.GA`, inlined so the SDK stays out of the initial bundle. */
const GA_PLATFORM = "ga";
const ORGANIZATION_NAME = "PHYSICS_WALLAH";
/** Upper bound on how long the analytics SDK waits for an idle main thread after load. */
const SDK_IDLE_TIMEOUT_MS = 3000;
/** An interaction means a CTA may fire next, so stop waiting and get the transport ready. */
const SDK_WAKE_EVENTS = ["pointerdown", "keydown"] as const;
const DEVICE_ID_KEY = "device_id";
const PSEUDO_USERID_COOKIE = "pseudo_userid";
const PI_PSEUDO_USERID_COOKIE = "pi_pseudo_userid";
/** `pi_pseudo_userid` is shared across PW hosts; anything else (localhost) gets a host-only cookie. */
const PI_COOKIE_BASE_DOMAINS = ["pw.live", "physicswallah.live"];
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "igshid",
] as const;

type EventParams = Record<string, unknown>;

type PiProfile = {
  _id?: string;
  cohortId?: string;
  class?: string;
  exams?: string[];
};

function readCookie(name: string) {
  const entry = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${name}=`));

  return entry ? entry.slice(name.length + 1) : "";
}

function getDeviceCategory() {
  if (window.innerWidth <= 768) return "mobile";
  if (window.innerWidth <= 1024) return "tablet";

  return "desktop";
}

function getDeviceId() {
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);

    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }

    return deviceId;
  } catch {
    // Blocked storage (private mode / SecurityError): track without a device id.
    return "";
  }
}

function getProfile(): PiProfile | null {
  const sdkUser = (
    window as unknown as {
      PWWebSDK?: { user?: { profileId?: PiProfile } | null };
    }
  ).PWWebSDK?.user;

  if (sdkUser?.profileId) {
    return sdkUser.profileId;
  }

  try {
    return JSON.parse(localStorage.getItem("user") || "null")?.profileId ?? null;
  } catch {
    return null;
  }
}

/** Cohort name is not stored directly; PW composes it from the primary exam and class. */
function getUserIdentityParams() {
  const profile = getProfile();

  return {
    user_id: profile?._id || "",
    pw_cohort_id: profile?.cohortId || "",
    pw_cohort_name: [profile?.exams?.[0] || "", profile?.class || ""]
      .filter(Boolean)
      .join("-"),
  };
}

/** Dedicated PI identity, bootstrapped on the first event from the shared cookie or the device id. */
function getPiPseudoUserid() {
  try {
    const existing = readCookie(PI_PSEUDO_USERID_COOKIE);

    if (existing) {
      return existing;
    }

    const value = readCookie(PSEUDO_USERID_COOKIE) || getDeviceId();

    if (value) {
      const base = PI_COOKIE_BASE_DOMAINS.find(
        (domain) =>
          window.location.hostname === domain ||
          window.location.hostname.endsWith(`.${domain}`),
      );
      const oneYearInSeconds = 60 * 60 * 24 * 365;

      document.cookie = `${PI_PSEUDO_USERID_COOKIE}=${value}; max-age=${oneYearInSeconds}${
        base ? `; domain=.${base}` : ""
      }; path=/`;
    }

    return value;
  } catch {
    // A malformed cookie on the shared domain must not break event tracking.
    return "";
  }
}

function getCampaignParams() {
  const search = new URLSearchParams(window.location.search);

  return Object.fromEntries(
    UTM_KEYS.map((key) => [key, search.get(key)]).filter(
      ([, value]) => Boolean(value),
    ),
  );
}

function getBaseParams() {
  return {
    source: "web",
    page_name: PI_LANDING_PAGE_NAME,
    platform: "WEB",
    device_category: getDeviceCategory(),
    login_status: getSessionToken() ? "logged_in" : "logged_out",
    landing_version: PI_LANDING_VERSION,
    organization_name: ORGANIZATION_NAME,
    device_id: getDeviceId(),
    pi_psuedouserid: getPiPseudoUserid(),
    ...getUserIdentityParams(),
    ...getCampaignParams(),
  };
}

let sdkGate: Promise<void> | undefined;

/**
 * Loading the SDK pulls GA and Firebase onto the main thread, which is the single biggest source of
 * blocking time on this page. Hold it back until the browser is idle or the visitor interacts —
 * whichever comes first — so it never competes with hydration. Events fired before then queue here.
 */
function whenSdkAllowed() {
  sdkGate ??= new Promise<void>((resolve) => {
    const release = () => {
      SDK_WAKE_EVENTS.forEach((type) =>
        window.removeEventListener(type, release),
      );
      resolve();
    };

    SDK_WAKE_EVENTS.forEach((type) =>
      window.addEventListener(type, release, { passive: true }),
    );

    const scheduleIdle = () => {
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(release, { timeout: SDK_IDLE_TIMEOUT_MS });
      } else {
        window.setTimeout(release, SDK_IDLE_TIMEOUT_MS);
      }
    };

    if (document.readyState === "complete") {
      scheduleIdle();
    } else {
      window.addEventListener("load", scheduleIdle, { once: true });
    }
  });

  return sdkGate;
}

function track(eventName: PiLandingEventType, eventParams: EventParams = {}) {
  if (typeof window === "undefined") {
    return;
  }

  const eventData = Object.fromEntries(
    Object.entries({ ...getBaseParams(), ...eventParams }).filter(
      ([, value]) => value !== undefined,
    ),
  );

  // The SDK owns the GA transport; awaiting it also queues events fired before it finishes loading.
  void whenSdkAllowed()
    .then(getWebSdk)
    .then((sdk) =>
      sdk?.eventService?.logEvent({ eventName, eventData, logTo: [GA_PLATFORM] }),
    )
    .catch((error) => console.error("[analytics] Unable to log event", error));
}

let hasTrackedPageView = false;
/**
 * Set by the "See plans" CTA so the pricing exposure is attributed to it instead of scroll. The
 * section name is stored too, so sections scrolled past on the way do not consume it.
 */
let pendingExposure: {
  sectionName: string;
  source: PiExposureSource;
} | null = null;
/** Exam preview currently rendered by the exam selector; read by CTA and section events. */
let currentSelectedExam = "";

function consumeExposureSource(sectionName: string): PiExposureSource {
  if (pendingExposure?.sectionName !== sectionName) {
    return PI_EXPOSURE_SOURCE.SCROLL;
  }

  const { source } = pendingExposure;
  pendingExposure = null;

  return source;
}

export function setExposureSource(
  sectionName: string,
  source: PiExposureSource,
) {
  pendingExposure = { sectionName, source };
}

export function setSelectedExam(exam: string) {
  currentSelectedExam = exam;
}

export const piEvents = {
  pageViewed: () => {
    if (hasTrackedPageView || typeof window === "undefined") {
      return;
    }

    hasTrackedPageView = true;

    let referrer = "";
    try {
      referrer = document.referrer ? new URL(document.referrer).hostname : "";
    } catch {
      // Non-absolute referrer; leave the hostname empty.
    }

    track(PiLandingEventType.PAGE_VIEWED, {
      source:
        new URLSearchParams(window.location.search).get("utm_source") ||
        "direct",
      referrer,
    });
  },

  sectionViewed: ({
    sectionName,
    sectionPosition,
  }: {
    sectionName: string;
    sectionPosition: number;
  }) =>
    track(PiLandingEventType.SECTION_VIEWED, {
      section_name: sectionName,
      section_position: sectionPosition,
      exposure_source: consumeExposureSource(sectionName),
      selected_exam: currentSelectedExam,
    }),

  ctaClicked: ({
    ctaName,
    ctaLocation,
    destination,
  }: {
    ctaName: PiCtaName;
    ctaLocation: PiCtaLocation;
    destination: PiDestination;
  }) =>
    track(PiLandingEventType.CTA_CLICKED, {
      cta_name: ctaName,
      cta_location: ctaLocation,
      destination,
      selected_exam: currentSelectedExam,
    }),

  loginAction: ({
    action,
    loginEntryPoint,
    step = "mobile_number",
    failureReason,
    closeMethod,
    selectedPlan,
  }: {
    action: PiLoginAction;
    loginEntryPoint: PiLoginEntryPoint;
    step?: "mobile_number" | "otp";
    failureReason?: string;
    closeMethod?: PiCloseMethod;
    selectedPlan?: string;
  }) =>
    track(PiLandingEventType.LOGIN_ACTION, {
      action,
      login_entry_point: loginEntryPoint,
      step,
      failure_reason: failureReason,
      close_method: closeMethod,
      selected_plan: selectedPlan,
    }),

  examSelected: ({
    selectedExam,
    previousExam,
    selectionPosition,
  }: {
    selectedExam: string;
    previousExam: string;
    selectionPosition: number;
  }) =>
    track(PiLandingEventType.EXAM_SELECTED, {
      selected_exam: selectedExam,
      previous_exam: previousExam,
      selection_position: selectionPosition,
    }),

  faqToggled: ({
    faqId,
    faqQuestion,
    faqPosition,
    action,
  }: {
    faqId: string;
    faqQuestion: string;
    faqPosition: number;
    action: "expanded" | "collapsed";
  }) =>
    track(PiLandingEventType.FAQ_TOGGLED, {
      faq_id: faqId,
      // GA drops event parameter values longer than 100 characters.
      faq_question: faqQuestion.slice(0, 100),
      faq_position: faqPosition,
      action,
    }),

  purchaseButtonClicked: ({
    planSelected,
    buttonVariant,
    planPosition,
    isRecommended,
    postClickDestination,
  }: {
    planSelected: EventParams;
    buttonVariant: string;
    planPosition: number;
    isRecommended: boolean;
    postClickDestination: "login" | "checkout";
  }) =>
    track(PiLandingEventType.PURCHASE_BUTTON_CLICKED, {
      plan_selected: JSON.stringify(planSelected),
      button_variant: buttonVariant,
      source: PI_LANDING_PAGE_NAME,
      plan_position: planPosition,
      is_recommended: isRecommended,
      post_click_destination: postClickDestination,
    }),
};
